import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Lock, TrendingUp, Shield, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { API, getAuthHeaders } from '../utils/api';

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFACode, setTwoFACode] = useState('');

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password
      }, { withCredentials: true });
      
      // Check if 2FA is required
      if (response.data.requires_2fa) {
        setRequires2FA(true);
        toast.info('Please enter your 2FA code');
      } else if (response.data.user_id) {
        // Store session token in localStorage for mobile browsers that block cookies
        if (response.data.session_token) {
          localStorage.setItem('session_token', response.data.session_token);
        }
        toast.success('Login successful!');
        navigate('/dashboard', { state: { user: response.data } });
      } else {
        toast.error('Unexpected response from server');
      }
    } catch (error) {
      console.error('Login error:', error);
      if (error.response) {
        toast.error(error.response.data?.detail || 'Invalid credentials');
      } else if (error.request) {
        toast.error('Network error. Please check your connection.');
      } else {
        toast.error('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handle2FALogin = async (e) => {
    e.preventDefault();
    if (!twoFACode || twoFACode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/2fa/verify-login`, {
        email,
        password,
        code: twoFACode
      }, { withCredentials: true });
      
      toast.success('Login successful!');
      navigate('/dashboard', { state: { user: response.data } });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid 2FA code');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#060A10] flex">
      {/* Left side - Login form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <div className="flex items-center gap-3 justify-center mb-6">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/df6b9931-f35a-4f03-9adf-4a4572c904c3/images/6dfa2d55642f0060455034e074f9f811d7710ca6e7452f73c1fd5e309a79f2ae.png"
                alt="TradeForge"
                className="w-12 h-12"
              />
              <span className="text-3xl font-bold font-['Manrope'] bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">TradeForge</span>
            </div>
            <h2 className="text-4xl font-extrabold font-['Manrope'] tracking-tight text-center mb-2">Welcome Back</h2>
            <p className="text-center text-slate-400">Sign in to access your trading dashboard</p>
          </div>

          <div className="bg-[#0D131C] border border-[#1E293B] rounded-md p-8">
            {requires2FA ? (
              <form onSubmit={handle2FALogin} className="space-y-6">
                <div className="text-center mb-4">
                  <div className="w-16 h-16 bg-[#00E676]/20 rounded-md flex items-center justify-center mx-auto mb-4">
                    <Shield className="w-8 h-8 text-[#00E676]" />
                  </div>
                  <h3 className="text-xl font-bold font-['Manrope']">Two-Factor Authentication</h3>
                  <p className="text-sm text-slate-400 mt-2">Enter the 6-digit code from your authenticator app</p>
                </div>
                
                <div>
                  <Input
                    type="text"
                    maxLength={6}
                    value={twoFACode}
                    onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    data-testid="2fa-login-code-input"
                    className="bg-[#060A10] border-[#1E293B] text-center text-3xl tracking-[0.5em] font-mono h-14 focus:border-cyan-500"
                    autoFocus
                  />
                </div>
                
                <Button
                  type="submit"
                  disabled={loading || twoFACode.length !== 6}
                  data-testid="2fa-login-submit-btn"
                  className="w-full bg-[#00E676] hover:bg-[#00E676]/90 text-black font-bold h-11 rounded-sm"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify & Login'
                  )}
                </Button>
                
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setRequires2FA(false); setTwoFACode(''); }}
                  className="w-full border-[#1E293B] hover:bg-[#152A4A] rounded-sm"
                >
                  Back to Login
                </Button>
              </form>
            ) : (
              <form onSubmit={handleEmailLogin} className="space-y-6">
                <div>
                  <Label htmlFor="email" className="text-slate-300 text-sm font-mono uppercase tracking-wider">Email</Label>
                  <div className="mt-2 relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      id="email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid="login-email-input"
                      className="pl-10 bg-[#060A10] border-[#1E293B] focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 text-white h-11 rounded-sm"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-slate-300 text-sm font-mono uppercase tracking-wider">Password</Label>
                    <Link to="/forgot-password" className="text-sm text-cyan-400 hover:text-cyan-300" data-testid="forgot-password-link">
                      Forgot password?
                    </Link>
                  </div>
                  <div className="mt-2 relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      id="password"
                      type="password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      data-testid="login-password-input"
                      className="pl-10 bg-[#060A10] border-[#1E293B] focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 text-white h-11 rounded-sm"
                      placeholder="••••••••"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="login-email-submit-btn"
                  className="w-full bg-cyan-500 hover:bg-cyan-400 text-black font-bold h-11 rounded-sm glow-cyan transition-all duration-150"
                >
                  {loading ? 'Signing in...' : 'Sign In with Email'}
                </Button>
              </form>
            )}
          </div>

          <p className="text-center text-slate-400">
            Don't have an account?{' '}
            <Link to="/signup" className="text-cyan-400 hover:text-cyan-300 font-medium">
              Sign up
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Image/branding */}
      <div 
        className="hidden lg:block lg:w-1/2 bg-cover bg-center relative"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1642790106117-e829e14a795f?q=80&w=1956&auto=format&fit=crop)' }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-900/80 to-blue-900/80 backdrop-blur-sm"></div>
        <div className="relative h-full flex items-center justify-center p-12">
          <div className="text-center">
            <h3 className="text-4xl font-extrabold font-['Manrope'] tracking-tight mb-4">Precision Trading Intelligence</h3>
            <p className="text-xl text-slate-200">Data-driven signals for disciplined traders</p>
          </div>
        </div>
      </div>
    </div>
  );
}
